import { Component, EventEmitter, Output, OnDestroy, Input, OnChanges, SimpleChanges } from '@angular/core';
import { environment } from '../../environments/environment';

@Component({
  selector: 'app-timepicker',
  standalone: true,
  templateUrl: './timepicker.component.html',
  styleUrl: './timepicker.component.scss'
})
export class TimepickerComponent implements OnDestroy, OnChanges {
  translationUrl = environment.translationUrl;
  /**
   * Estado interno del componente.
   * - Si allowEmpty=true, hour/minute pueden ser null para representar "--:--".
   * - Si no, siempre deberían estar inicializados a algún valor válido.
   */
  hour: number | null = null;
  minute: number | null = null;
  @Input() value: string | null = null; // 'HH:mm' o null

  /**
   * Permite el estado vacío "--:--".
   * Si es false, el componente se auto-inicializa a un valor válido (minTime o 00:00).
   */
  @Input() allowEmpty = true;

  /**
   * Límites opcionales en formato "HH:mm".
   * Se usan para "clamp" (recortar) el valor actual dentro del rango permitido.
   */
  @Input() minTime: string | null = null;
  @Input() maxTime: string | null = null;

  /**
   * Paso con el que aumentan/disminuyen los minutos.
   * Ej: minuteStep=5 -> 00:00, 00:05, 00:10...
   */
  @Input() minuteStep = 1;

  /**
   * Salida del componente:
   * - Emite "HH:mm" cuando hay un valor seleccionado
   * - Emite null cuando está en modo vacío "--:--"
   */
  @Output() timeChange = new EventEmitter<string | null>();

  // --- Timers para la funcionalidad de "mantener pulsado" (auto-repeat) ---
  private holdTimeoutId: number | null = null;   // delay antes de empezar a repetir
  private holdIntervalId: number | null = null;  // intervalo de repetición
  private holdAction: (() => void) | null = null; // acción que se repetirá mientras se mantenga pulsado

  /**
   * Se ejecuta cuando cambian Inputs (minTime/maxTime).
   * Si el valor actual está fuera del nuevo rango, lo ajustamos.
   *
   * Importante:
   * - Si el componente está vacío y allowEmpty=true, NO lo rellenamos automáticamente.
   * - Si allowEmpty=false, sí inicializamos para que siempre haya un valor válido.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value']) {
      this.setFromValue(this.value); // parsea HH:mm y asigna hour/minute (o null)
    }
    if (changes['minTime'] || changes['maxTime']) {
      if (!this.isEmpty) {
        const before = this.formattedTime;
        this.clampToLimits();
        // Si el clamp modificó el valor, notificamos al padre
        if (before !== this.formattedTime) this.emit();
      } else if (!this.allowEmpty) {
        // Si no se permite vacío, forzamos un valor inicial válido
        this.ensureInitialized();
      }
    }
  }

  /**
   * Cleanup: evitamos timers colgando si el componente se destruye.
   */
  ngOnDestroy(): void {
    this.stopHold();
  }

  /**
   * Devuelve si el timepicker está en estado vacío "--:--".
   */
  get isEmpty(): boolean {
    return this.hour === null || this.minute === null;
  }

  /**
   * Devuelve el string "HH:mm" siempre con dos dígitos, o null si está vacío.
   * Este es el valor que emitimos hacia el componente padre.
   */
  get formattedTime(): string | null {
    if (this.isEmpty) return null;
    return `${String(this.hour).padStart(2, '0')}:${String(this.minute).padStart(2, '0')}`;
  }

  /**
   * Permite deshabilitar botones "down".
   * Si está vacío, devolvemos true para que el primer click pueda inicializar el valor.
   */
  get canDec(): boolean {
    if (this.isEmpty) return true;
    const min = this.parseToMinutes(this.minTime);
    return min === null || this.totalMinutes > min;
  }

  /**
   * Permite deshabilitar botones "up".
   * Si está vacío, devolvemos true para que el primer click pueda inicializar el valor.
   */
  get canInc(): boolean {
    if (this.isEmpty) return true;
    const max = this.parseToMinutes(this.maxTime);
    return max === null || this.totalMinutes < max;
  }

  /**
   * Hora actual en minutos totales (0..1439).
   * Si está vacío, trata null como 0 (solo se usa cuando ya inicializamos).
   */
  private get totalMinutes(): number {
    return (this.hour ?? 0) * 60 + (this.minute ?? 0);
  }

  /**
   * Emite el valor actual al padre.
   * - "HH:mm" si hay valor
   * - null si está vacío
   */
  private emit() {
    this.timeChange.emit(this.formattedTime);
  }

  /**
   * Inicializa el valor cuando está vacío.
   *
   * UX:
   * - Si el usuario pulsa "UP": inicializamos al mínimo (minTime o 00:00)
   * - Si el usuario pulsa "DOWN": inicializamos al máximo (maxTime o 23:59)
   *
   * Luego aplicamos clamp y emitimos.
   */
  private ensureInitialized(direction: 'up' | 'down' = 'up') {
    if (!this.isEmpty) return;

    if (direction === 'down') {
      const max = this.parseToMinutes(this.maxTime);
      const start = max ?? (23 * 60 + 59);
      this.hour = Math.floor(start / 60);
      this.minute = start % 60;
    } else {
      const min = this.parseToMinutes(this.minTime);
      const start = min ?? 0;
      this.hour = Math.floor(start / 60);
      this.minute = start % 60;
    }

    this.clampToLimits();
    this.emit();
  }

  /**
   * Limpia el valor (vuelve a "--:--") si allowEmpty=true.
   * Además corta el "hold" si estaba activo.
   */
  clear() {
    if (!this.allowEmpty) return;
    this.hour = null;
    this.minute = null;
    this.stopHold();
    this.emit(); // emite null al padre
  }

  // ---------- Acciones de incremento/decremento ----------
  // Nota: siempre llamamos ensureInitialized() para que si estaba "--:--",
  // el primer click convierta el estado a un valor real.

  addHours() {
    this.ensureInitialized();
    this.setTotalMinutes(this.totalMinutes + 60);
  }

  decreaseHours() {
    this.ensureInitialized();
    this.setTotalMinutes(this.totalMinutes - 60);
  }

  addMinutes(step = this.minuteStep) {
    this.ensureInitialized();
    this.setTotalMinutes(this.totalMinutes + step);
  }

  decreaseMinutes(step = this.minuteStep) {
    this.ensureInitialized();
    this.setTotalMinutes(this.totalMinutes - step);
  }

  // ---------- HOLD (mantener pulsado para auto-repeat) ----------
  /**
   * Arranca el modo "mantener pulsado":
   * - ejecuta una acción inmediatamente (1 click)
   * - espera initialDelayMs
   * - repite cada intervalMs
   * - acelera a fastIntervalMs después de accelerateAfterMs
   *
   * Además, si se llega a un límite (min/max), se detiene automáticamente.
   */
  startHold(actionName: 'H_UP' | 'H_DOWN' | 'M_UP' | 'M_DOWN') {
    // Si está vacío, inicializamos con dirección coherente con el botón pulsado
    if (this.isEmpty) {
      this.ensureInitialized(
        actionName === 'H_DOWN' || actionName === 'M_DOWN'
          ? 'down'
          : 'up'
      );
    }

    // Si ya estamos en el límite, no arrancamos el hold
    if ((actionName === 'H_UP' || actionName === 'M_UP') && !this.canInc) return;
    if ((actionName === 'H_DOWN' || actionName === 'M_DOWN') && !this.canDec) return;

    // Asignamos qué acción concreta se repetirá
    this.holdAction =
      actionName === 'H_UP' ? () => this.addHours() :
        actionName === 'H_DOWN' ? () => this.decreaseHours() :
          actionName === 'M_UP' ? () => this.addMinutes(this.minuteStep) :
            () => this.decreaseMinutes(this.minuteStep);

    // Click inmediato
    this.holdAction();

    // Si ya había hold activo, lo reiniciamos (pero mantenemos la acción)
    this.stopHold(false);

    // Ajustes de timings
    const initialDelayMs = 250;     // retardo antes de empezar a repetir
    const intervalMs = 90;          // repetición normal
    const accelerateAfterMs = 900;  // cuándo acelerar
    const fastIntervalMs = 45;      // repetición rápida al acelerar

    const startTime = Date.now();

    // Tras el delay, empezamos a repetir
    this.holdTimeoutId = window.setTimeout(() => {
      this.holdIntervalId = window.setInterval(() => {
        // Si al repetir ya no se puede avanzar/retroceder, paramos
        if ((actionName === 'H_UP' || actionName === 'M_UP') && !this.canInc) { this.stopHold(); return; }
        if ((actionName === 'H_DOWN' || actionName === 'M_DOWN') && !this.canDec) { this.stopHold(); return; }

        this.holdAction?.();

        // Aceleración simple después de cierto tiempo
        const elapsed = Date.now() - startTime;
        if (elapsed > accelerateAfterMs && this.holdIntervalId) {
          window.clearInterval(this.holdIntervalId);
          this.holdIntervalId = window.setInterval(() => {
            if ((actionName === 'H_UP' || actionName === 'M_UP') && !this.canInc) { this.stopHold(); return; }
            if ((actionName === 'H_DOWN' || actionName === 'M_DOWN') && !this.canDec) { this.stopHold(); return; }
            this.holdAction?.();
          }, fastIntervalMs);
        }
      }, intervalMs);
    }, initialDelayMs);
  }

  /**
   * Para el modo hold:
   * - limpia timeout/interval activos
   * - opcionalmente limpia la acción almacenada (por defecto sí)
   */
  stopHold(clearAction: boolean = true) {
    if (this.holdTimeoutId) { window.clearTimeout(this.holdTimeoutId); this.holdTimeoutId = null; }
    if (this.holdIntervalId) { window.clearInterval(this.holdIntervalId); this.holdIntervalId = null; }
    if (clearAction) this.holdAction = null;
  }

  // ---------- Core de cálculo y límites ----------
  /**
   * Setea la hora a partir de "minutos totales".
   * Normaliza a rango 0..1439 (wrap-around).
   * Luego aplica clamp (min/max) y emite.
   */
  private setTotalMinutes(total: number) {
    // Normaliza con wrap-around (ej: -1 => 1439)
    const normalized = (total % 1440 + 1440) % 1440;
    this.hour = Math.floor(normalized / 60);
    this.minute = normalized % 60;

    this.clampToLimits();
    this.emit();
  }

  /**
   * Aplica minTime/maxTime al estado actual:
   * - si t < min => t = min
   * - si t > max => t = max
   */
  private clampToLimits() {
    if (this.isEmpty) return;

    const min = this.parseToMinutes(this.minTime);
    const max = this.parseToMinutes(this.maxTime);

    let t = this.totalMinutes;

    if (min !== null && t < min) t = min;
    if (max !== null && t > max) t = max;

    this.hour = Math.floor(t / 60);
    this.minute = t % 60;
  }

  /**
   * Parsea "HH:mm" -> minutos totales (0..1439).
   * Devuelve null si:
   * - v es null/empty
   * - formato no es "HH:mm"
   * - valores fuera de rango
   */
  private parseToMinutes(v: string | null): number | null {
    if (!v) return null;
    const parts = v.split(':');
    if (parts.length !== 2) return null;

    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    if (h < 0 || h > 23 || m < 0 || m > 59) return null;

    return h * 60 + m;
  }

  private setFromValue(v: string | null) {
    if (!v) { this.hour = null; this.minute = null; return; }
    const [hh, mm] = v.split(':');
    this.hour = Number(hh);
    this.minute = Number(mm);
    if (Number.isNaN(this.hour) || Number.isNaN(this.minute)) { this.hour = null; this.minute = null; }
    this.clampToLimits();
  }

}
